import * as React from 'react';
import * as styles from './help.module.scss';
import HelpDocument from '../../models/helpDocument';
import { NavLink } from 'react-router-dom';
import Paths from '../../models/enums/paths';
import useHelp from '../../hooks/useHelp';
import clsx from 'clsx';
import { KeyboardArrowDown } from '@material-ui/icons';
import { Icon } from '@material-ui/core';

interface Props {
  parentDocument: HelpDocument;
  documents: HelpDocument[];
  activeDocument: number;
  margin: number;
  setActiveDocument: (activeDocument: number) => void;
  setIsSearching: (searching: boolean) => void;
}

/**
 * Panel
 * @function Panel - Individual panel for the left toolbar of the Help Screen
 */
const Panel = ({ parentDocument, documents, margin, activeDocument, setActiveDocument, setIsSearching }: Props) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(true);
  const { changeActiveDoc, renderChildren, getChildDocsToRender, isActiveDoc, isTopLevelDoc } = useHelp();

  /** @const childDocs - Array of the current documents children */
  const childDocs = documents.filter(document => document.parentGroupId === parentDocument.snippetGroupId);
  let activeDocs = [];
  const selectedDocument = documents.find(doc => doc.snippetGroupId === activeDocument);
  if (selectedDocument) {
    let currentDocument = selectedDocument;

    // Runs until root document is found
    while (currentDocument.parentGroupId !== 0) {
      currentDocument = documents.find(doc => doc.snippetGroupId === currentDocument.parentGroupId);
      if (currentDocument === undefined) {
        currentDocument = selectedDocument;
        break;
      }
    }
    activeDocs = getChildDocsToRender(documents, currentDocument, [currentDocument]);
  }

  const isActive = isActiveDoc(parentDocument, activeDocs);
  const topLevelDoc = isTopLevelDoc(parentDocument);

  // Returns current document and child documents if child documents exist
  if (childDocs.length > 0) {
    return (
      <div className={clsx(styles.collapsibleSection, !isOpen && styles.collapsibleSectionClosed)}>
        <div
          className={clsx(styles.parent, isActive && styles.active, topLevelDoc && styles.isTopLevelDoc)}
          style={{ paddingLeft: margin }}
        >
          <div className={styles.collapsibleParentContent}>
            <Icon className={styles.collapseIcon}>
              <KeyboardArrowDown onClick={() => setIsOpen(open => !open)} />
            </Icon>
            <NavLink
              to={{
                pathname: `${Paths.Help}/${parentDocument.snippetGroupId}`,
                state: { id: parentDocument.snippetGroupId }
              }}
              onClick={() => setIsSearching(false)}
              isActive={(param, location) =>
                changeActiveDoc(param, location, parentDocument.snippetGroupId, setActiveDocument)
              }
              className={clsx(styles.navLink, isActive && styles.activeLink)}
            >
              {parentDocument.title}
            </NavLink>
          </div>
        </div>
        <div className={styles.children}>
          {renderChildren(childDocs, margin, documents, activeDocument, setActiveDocument, setIsSearching)}
        </div>
      </div>
    );
  }

  // Returns if document has no more children
  return (
    <div
      className={clsx(styles.documentSection, isActive && styles.active, topLevelDoc && styles.isTopLevelDoc)}
      style={{ paddingLeft: margin }}
    >
      <NavLink
        to={{
          pathname: `${Paths.Help}/${parentDocument.snippetGroupId}`,
          state: { id: parentDocument.snippetGroupId }
        }}
        onClick={() => setIsSearching(false)}
        isActive={(param, location) =>
          changeActiveDoc(param, location, parentDocument.snippetGroupId, setActiveDocument)
        }
        className={clsx(styles.navLink, isActive && styles.activeLink)}
      >
        {parentDocument.title}
      </NavLink>
    </div>
  );
};

export default Panel;
